// One source -> one channel: reuse the user's channel that already carries the
// source's name, create it when there is none. The review lets the user
// override each source (any owned channel, or a new one with its own name).
import { normalizeNickname } from './mappingNickname';

export const NEW_CHANNEL = '__new__';

export interface OwnedChannel {
  nickname: string;
  title: string;
}

export interface SourceChannel {
  nickname: string; // the existing channel's, or the one that will be created
  title: string;
  isNew: boolean;
  matched: boolean; // an existing channel was found by name (not picked by the user)
}

function uniqueNickname(title: string, taken: Set<string>): string {
  const base = normalizeNickname(title) || '@channel';
  let nick = base;
  for (let n = 2; taken.has(nick.toLowerCase()); n++) nick = `${base.slice(0, 36)}-${n}`;
  taken.add(nick.toLowerCase());
  return nick;
}

// Names a source's channel could already go by: "<mapping> · <entity>", the
// bare entity ("Apple"), or - for a single source - the mapping's own title.
export function candidateNames(title: string, label: string): string[] {
  return label ? [`${title} · ${label}`, label] : [title];
}

function findOwned(names: string[], owned: OwnedChannel[]): OwnedChannel | undefined {
  const nicks = names.map((n) => normalizeNickname(n)).filter(Boolean);
  const titles = names.map((n) => n.trim().toLowerCase());
  return owned.find((c) => nicks.includes(normalizeNickname(c.nickname)) || titles.includes((c.title ?? '').trim().toLowerCase()));
}

// labels: one per source ('' for a webhook, which is a single source).
// choice[i]: an owned nickname, or NEW_CHANNEL; absent = the default (match by name, else new).
// names[i]: the title to give a NEW channel (blank = the default name).
export function assignChannels(title: string, labels: string[], owned: OwnedChannel[], choice: Record<number, string>, names: Record<number, string>): SourceChannel[] {
  const taken = new Set(owned.map((c) => c.nickname.toLowerCase()));
  return labels.map((label, i) => {
    const cands = candidateNames(title || 'AI integration', label);
    const picked = choice[i];
    if (picked && picked !== NEW_CHANNEL) {
      const c = owned.find((o) => o.nickname === picked);
      if (c) return { nickname: c.nickname, title: c.title, isNew: false, matched: false };
    }
    if (!picked) {
      const hit = findOwned(cands, owned);
      if (hit) return { nickname: hit.nickname, title: hit.title, isNew: false, matched: true };
    }
    const t = names[i]?.trim() || cands[0];
    return { nickname: uniqueNickname(t, taken), title: t, isNew: true, matched: false };
  });
}
